/**
 * The one-time "this is new" pop-up (constants/featureIntros.js decides
 * which, and to whom). Shows on open, a moment after the page settles, and
 * never on top of the new-layout tour. Marked seen as soon as it appears,
 * so closing the tab doesn't bring it back.
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight } from "lucide-react";
import { GOLD } from "../constants/theme";
import { FEATURES, dmsAvailable, mapAvailable } from "../utils/featureFlags";
import { isNative } from "../../utils/platform";
import { loadUserData, saveUserData } from "../utils/userData";
import { nextIntro, SEEN_INTROS_KEY } from "../constants/featureIntros";

const LOCAL = "bam_seen_intros";
const readLocal = () => { try { return JSON.parse(localStorage.getItem(LOCAL) || "[]"); } catch { return []; } };

export function FeatureIntro({ enabled, joinedAt, onAction }) {
  const [intro, setIntro] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    let timer = null;
    const pick = async () => {
      let remote = null;
      try { remote = await loadUserData(SEEN_INTROS_KEY, null); } catch { /* offline */ }
      const seen = Array.from(new Set([...(Array.isArray(remote) ? remote : []), ...readLocal()]));
      const features = { ...FEATURES, memberMap: mapAvailable() };
      const next = nextIntro({ joinedAt, seen, features, native: isNative(), dms: dmsAvailable() });
      if (!live || !next) return;
      // The new-layout tour goes first; try again once it's closed.
      if (window.__bamLayoutTourOpen) { timer = setTimeout(pick, 5000); return; }
      const now = [...seen, next.id];
      try { localStorage.setItem(LOCAL, JSON.stringify(now)); } catch { /* private window */ }
      Promise.resolve(saveUserData(SEEN_INTROS_KEY, now)).catch(() => {});
      setIntro(next);
    };
    timer = setTimeout(pick, 2500);
    return () => { live = false; clearTimeout(timer); };
  }, [enabled, joinedAt]);

  if (!intro) return null;
  const c = intro.copy;
  const close = () => setIntro(null);

  return createPortal((
    <div role="dialog" aria-modal="true" aria-label={c.title} onClick={close} className="bam-scrim-in"
      style={{ position: "fixed", inset: 0, zIndex: 7500, background: "rgba(0,0,0,0.65)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="bam-dialog-in"
        style={{ position: "relative", width: "100%", maxWidth: 440, borderRadius: 18, padding: "26px 24px 22px",
          background: "#121212", border: `1px solid ${GOLD}55`, boxShadow: "0 30px 80px rgba(0,0,0,0.6)", color: "#F5F5F5",
          fontFamily: "'DM Sans', sans-serif" }}>
        <button type="button" onClick={close} aria-label="Close"
          style={{ position: "absolute", top: 12, right: 12, width: 40, height: 40, borderRadius: 20, border: "none",
            background: "transparent", color: "#9CA3AF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={18}/>
        </button>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: GOLD, marginBottom: 8 }}>{c.eyebrow}</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, letterSpacing: 1.2, lineHeight: 1.05, marginBottom: 12, paddingRight: 28 }}>{c.title}</div>
        <div style={{ fontSize: 14.5, color: "#D4D4D4", lineHeight: 1.6, marginBottom: c.note ? 10 : 20 }}>{c.body}</div>
        {c.note && <div style={{ fontSize: 12.5, color: "#9CA3AF", lineHeight: 1.5, marginBottom: 20 }}>{c.note}</div>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="btn" onClick={() => { close(); onAction?.(intro.action); }}
            style={{ minHeight: 44, padding: "0 18px", borderRadius: 10, border: "none", background: GOLD, color: "#111",
              fontSize: 14, fontWeight: 800, fontFamily: "inherit", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
            {c.primary} <ArrowRight size={15}/>
          </button>
          <button type="button" className="btn" onClick={close}
            style={{ minHeight: 44, padding: "0 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)",
              background: "transparent", color: "#C9C9C9", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

export default FeatureIntro;
